import { Card, Form, Space, Spin, theme } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { WrenchAdjustableCircle } from 'react-bootstrap-icons';
import { useIsFetching } from 'react-query';

import { AppMessage } from '@/common/components';
import { FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  SearchConsoleAccountProcessStatus,
  SiteDomainsSearchConsoleProcessingStatusTag,
  SiteSearchConsoleFormFields,
  SiteSearchConsoleFormInputs,
  useAddSearchConsoleAccountToSiteMutation,
  useRemoveSearchConsoleAccountToSiteMutation
} from '@/modules/Domains';
import { useUpdateSearchConsoleAccountInSiteMutation } from '@/modules/Domains/queries';
import { Site, SitesQueryKeys } from '@/modules/Sites';

import { SearchConsoleAccountSiteSettingForm } from './SearchConsoleAccountSiteSettingForm';
import { SearchConsoleAccountSiteView } from './SearchConsoleAccountSiteView';
import { siteSearchConsoleFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  processStatus: Site['searchConsoleAccountProcessStatus'];
  searchConsoleAccount: Site['searchConsoleAccount'];
}

export const SearchConsoleAccountSiteSettingContainer: FC<Props> = memo(({ siteId, searchConsoleAccount, processStatus }) => {
  const appMessageApi = AppMessage.useControls();
  const { token } = theme.useToken();

  const [form] = Form.useForm<SiteSearchConsoleFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SiteSearchConsoleFormInputs>>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  const formSearchConsoleAccountId = Form.useWatch(SiteSearchConsoleFormInputs.SearchConsoleAccountId, form);

  const { mutate: addAccountToSiteMutation, isLoading: addAccountToSiteIsLoading } = useAddSearchConsoleAccountToSiteMutation(
    siteId
  )({
    onSuccess: () => {
      setMode('view');
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SiteSearchConsoleFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const { mutate: updateAccountInSiteMutation, isLoading: updateAccountInSiteIsLoading } = useUpdateSearchConsoleAccountInSiteMutation(
    siteId
  )({
    onSuccess: () => {
      setMode('view');
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SiteSearchConsoleFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const {
    mutate: removeAccountFromSiteMutation,
    isLoading: removeAccountFromSiteIsLoading
  } = useRemoveSearchConsoleAccountToSiteMutation(siteId)({
    onSuccess: () => {
      form.setFieldValue(SiteSearchConsoleFormInputs.SearchConsoleAccountId, undefined);

      setMode('view');
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SiteSearchConsoleFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: SiteSearchConsoleFormFields = {
    searchConsoleAccountId: searchConsoleAccount?.id || undefined
  };

  const handleFinish = useCallback((values: SiteSearchConsoleFormFields) => {
    setFormErrors(null);

    if (searchConsoleAccount && values.searchConsoleAccountId === searchConsoleAccount.id) {
      setMode('view');
    } else {

      if (searchConsoleAccount?.id) {
        updateAccountInSiteMutation(values);
      } else {
        addAccountToSiteMutation(values);
      }
    }
  }, [searchConsoleAccount]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleView = useCallback((): void => {
    form.resetFields();
    setFormErrors(null);
    setMode('view');
  }, []);

  const handleEdit = useCallback((): void => {
    setMode('edit');
  }, []);

  const handleDelete = useCallback((): void => {
    setFormErrors(null);
    removeAccountFromSiteMutation();
  }, []);

  const handleClearErrors = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const isSiteDossierLoading = !!useIsFetching([SitesQueryKeys.Dossier, siteId], { exact: true });

  const disabledByStatuses =
    processStatus === SearchConsoleAccountProcessStatus.Pending ||
    processStatus === SearchConsoleAccountProcessStatus.Set ||
    processStatus === SearchConsoleAccountProcessStatus.Unset;

  const iconColor = (() => {
    if (processStatus === SearchConsoleAccountProcessStatus.Setted) return token.colorSuccess;
    if (processStatus === SearchConsoleAccountProcessStatus.Failed) return token.colorError;
    return undefined;
  })();

  return (
    <>
      <Card
        size="small"
        title="Search Console"
        styles={{ body: { minHeight: 40 } }}
        extra={
          <Space>
            <SiteDomainsSearchConsoleProcessingStatusTag searchConsoleAccountProcessStatus={ processStatus } searchConsoleAccount={ searchConsoleAccount }/>

            <WrenchAdjustableCircle
              color={ iconColor }
              size={ 18 }
              style={{ verticalAlign: 'text-top' }}
            />
          </Space>
        }
      >
        <Spin spinning={ disabledByStatuses } style={{ minHeight: 40 }}>
          {
            (mode === 'view' && !!searchConsoleAccount) && (
              <SearchConsoleAccountSiteView
                searchConsoleAccount={ searchConsoleAccount }
                isAccountDeleting={ removeAccountFromSiteIsLoading }
                isButtonsDisabled={
                  isSiteDossierLoading ||
                  addAccountToSiteIsLoading ||
                  updateAccountInSiteIsLoading ||
                  removeAccountFromSiteIsLoading ||
                  disabledByStatuses
                }
                handleEdit={ handleEdit }
                handleDelete={ handleDelete }
              />
            )
          }
          {
            (mode === 'edit' || !searchConsoleAccount) && (
              <SearchConsoleAccountSiteSettingForm
                form={ form }
                formErrors={ formErrors }
                formName="site-search-console-account-setting-form"
                formType="edit"
                initialValues={ initialValues }
                formSearchConsoleAccountId={ formSearchConsoleAccountId as string }
                isFormLoading={ addAccountToSiteIsLoading }
                validationSchema={ siteSearchConsoleFormValidationSchema }
                handleBack={ (): void => {} }
                isButtonsDisabled={
                  isSiteDossierLoading ||
                  addAccountToSiteIsLoading ||
                  updateAccountInSiteIsLoading ||
                  removeAccountFromSiteIsLoading ||
                  disabledByStatuses
                }
                handleClearErrors={ handleClearErrors }
                handleView={ handleView }
                handleFinish={ handleFinish }
                handleFinishFailed={ handleFinishFailed }
              />
            )
          }
        </Spin>
      </Card>
    </>
  );
});
